import { collectPersonPublicationPathIdCandidates } from '@/lib/personPublicationPathIds';
import {
  ACCESS_PEOPLE_KEYS,
  FOURSCIENCE_KEYS,
  TESTWE_KEYS,
  deriveLearnwiseTargetProductValue,
  getCayuseTargetProductListForContext,
  getEvasysTargetProductListForContext,
  isAccessPeopleProductContextTenant,
  isFourScienceProductContextTenant,
  isTestweProductContextTenant,
  isSysExamProductContextTenant,
} from '@/lib/accountTargetProducts';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

const CAYUSE_PRODUCT_CONTEXTS = ['GRAD', 'COMPLIANCE', 'SPONSOR'];

const OUTREACH_ASSETS_LOG = '[NeoOutreachAssets]';
/** Bump when changing asset-load logic — confirms prod bundle in DevTools filter. */
export const OUTREACH_ASSETS_DEBUG_BUILD = '2026-05-20-v2';

/** Always logs for now; set `localStorage.neo_debug_outreach_assets='0'` to silence. */
export function isOutreachAssetsDebugEnabled() {
  try {
    return localStorage.getItem('neo_debug_outreach_assets') !== '0';
  } catch {
    return true;
  }
}

function logOutreachAssets(step, detail) {
  if (!isOutreachAssetsDebugEnabled()) return;
  console.log(OUTREACH_ASSETS_LOG, OUTREACH_ASSETS_DEBUG_BUILD, step, detail);
}

// One-time proof the educationalResources module loaded (filter: NeoOutreachAssets)
if (typeof window !== 'undefined') {
  console.log(OUTREACH_ASSETS_LOG, OUTREACH_ASSETS_DEBUG_BUILD, 'module loaded');
}

function summarizeAsset(asset) {
  if (!asset) return null;
  const url = resolveAssetUrl(asset);
  return {
    type: asset.type,
    name: String(asset.name ?? asset.title ?? '').slice(0, 48),
    url: url ? String(url).slice(0, 72) : null,
    isReading: canonicalComposerAssetType(asset?.type) === 'educational_resource',
  };
}

/** Lowercase tenant id for educational-resource product_context rules. */
export function normalizeEducationalTenantId(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase();
}

/**
 * Product_context values valid for this tenant (editor + API).
 * null = tenant does not use product_context on educational resources.
 */
export function getEducationalProductContextsForTenant(tenantId) {
  const tenant = normalizeEducationalTenantId(tenantId);
  if (isSysExamProductContextTenant(tenant)) return ['SYS', 'EXAM', 'ALL'];
  if (tenant === 'learnwise' || tenant === 'gravyty') return ['SUPPORT', 'FEEDBACK', 'TUTOR', 'ALL'];
  if (tenant === 'cayuse') return ['GRAD', 'COMPLIANCE', 'SPONSOR', 'ALL'];
  /* [2026-10-15] access_people — THE SAME FALL-THROUGH SettingsPersonalization.jsx ALREADY
   * DOCUMENTS, in a second file. That page was fixed on the 13th so the per-product Product Name
   * and Core Value Proposition fields render; this map was not, so Educational Resources stayed
   * the ONE section on the page with no Product selector while KPIs, hooks, case studies and
   * product links all had one. Caught by reading the rendered page rather than the code: every
   * other section printed "Product (optional)" and this one printed only Title and url.
   *
   * Keys match config/productContexts.ts and ACCESS_PEOPLE_KEYS. ALL is the sentinel for an asset
   * that suits either product — a warm-up article about payroll compliance is not HCM-only. */
  if (isAccessPeopleProductContextTenant(tenant)) return [...ACCESS_PEOPLE_KEYS, 'ALL'];
  // [2026-10-18] 4science: CRIS / REPOSITORY / GLAM, its three lens categories.
  if (isFourScienceProductContextTenant(tenant)) return [...FOURSCIENCE_KEYS, 'ALL'];
  // [2026-10-18] testwe: ONLINE_EXAMS / PROCTORING / MARKING, its three lens categories.
  if (isTestweProductContextTenant(tenant)) return [...TESTWE_KEYS, 'ALL'];
  return null;
}

export function tenantUsesEducationalProductContext(tenantId) {
  return getEducationalProductContextsForTenant(tenantId) != null;
}

/** Unset product tag on personalization/DIY assets defaults to ALL. */
export function normalizeProductAlignmentTag(raw) {
  const upper = String(raw ?? '').trim().toUpperCase();
  if (!upper || upper === 'ALL') return 'ALL';
  return upper;
}

/** Product keys from Settings → products list (excludes ALL sentinel). */
export function getCatalogProductKeysForDiy(products) {
  return (products || [])
    .map((p) => normalizeProductAlignmentTag(p?.productKey ?? p?.product_key ?? p?.id))
    .filter((k) => k && k !== 'ALL');
}

export function isAllProductsSelectedForDiy(selectedProductKeys, catalogProductKeys) {
  const selected = new Set(
    (selectedProductKeys || []).map((k) => normalizeProductAlignmentTag(k)).filter(Boolean),
  );
  if (selected.has('ALL')) return true;
  const catalog = (catalogProductKeys || []).filter((k) => k && k !== 'ALL');
  if (catalog.length === 0) return false;
  return catalog.every((k) => selected.has(k));
}

/**
 * Map playbook product checkboxes to canonical catalog keys (uppercase product_key).
 * @param {string[]} selectedProductKeys
 * @param {Array<{ id?: string, productKey?: string, product_key?: string }>} catalogProducts
 */
export function resolveSelectedProductKeysForDiyAlignment(
  selectedProductKeys,
  catalogProducts,
) {
  const catalogKeys = getCatalogProductKeysForDiy(catalogProducts);
  const catalogSet = new Set(catalogKeys);
  if (!Array.isArray(selectedProductKeys) || selectedProductKeys.length === 0) {
    return [];
  }

  const resolved = new Set();
  for (const raw of selectedProductKeys) {
    const n = normalizeProductAlignmentTag(raw);
    if (n === 'ALL') {
      catalogKeys.forEach((k) => resolved.add(k));
      continue;
    }
    if (catalogSet.has(n)) {
      resolved.add(n);
      continue;
    }
    for (const p of catalogProducts || []) {
      const id = normalizeProductAlignmentTag(p?.id);
      const pk = normalizeProductAlignmentTag(p?.productKey ?? p?.product_key);
      if ((n === id || n === pk) && pk !== 'ALL') {
        resolved.add(pk);
        break;
      }
    }
    if (catalogKeys.length === 0 && n !== 'ALL') resolved.add(n);
  }
  return [...resolved];
}

/** When localStorage.neo_debug_diy_product_alignment=1, log filter decisions in the DIY form. */
export function isDiyProductAlignmentDebugEnabled() {
  try {
    return localStorage.getItem('neo_debug_diy_product_alignment') === '1';
  } catch {
    return false;
  }
}

/**
 * Strict alignment: no product selected → no assets. With selection, only assets
 * tagged for selected products pass. Untagged / ALL-tagged rows are hidden unless
 * every catalog product is selected.
 *
 * @param {unknown[]} assets
 * @param {string[]} selectedProductKeys — raw checkbox values
 * @param {Array<{ id?: string, productKey?: string, product_key?: string }>} catalogProducts
 */
export function filterAssetsForDiyProductAlignment(
  assets,
  selectedProductKeys,
  catalogProducts,
) {
  if (!Array.isArray(assets)) return [];
  const selected = resolveSelectedProductKeysForDiyAlignment(
    selectedProductKeys,
    catalogProducts,
  );

  if (selected.length === 0) return [];

  const selectedSet = new Set(selected);
  const catalogKeys = getCatalogProductKeysForDiy(catalogProducts);

  return assets.filter((a) => {
    const pkRaw = a?.product_key ?? a?.productKey ?? a?.product_context ?? null;
    if (!pkRaw || String(pkRaw).trim() === '') return false;
    const normalized = normalizeProductAlignmentTag(pkRaw);
    if (normalized === 'ALL') {
      return isAllProductsSelectedForDiy(selectedProductKeys, catalogKeys);
    }
    return selectedSet.has(normalized);
  });
}

/**
 * Whether a personalization row is available for the playbook's product alignment selection.
 */
export function assetMatchesDiyProductAlignment(item, selectedProductKeys, catalogProducts) {
  if (item == null) return false;
  return filterAssetsForDiyProductAlignment(
    [item],
    selectedProductKeys,
    catalogProducts,
  ).length > 0;
}

export function normalizeEducationalResourceRow(item, tenantId) {
  const title = String(item?.title ?? item?.name ?? '').trim().slice(0, 200);
  const url = String(item?.url ?? '').trim();
  let pc = item?.product_context ?? item?.product_key ?? null;
  if (pc === '' || pc === undefined) pc = null;
  const upper = String(pc ?? '').trim().toUpperCase();
  const valid = getEducationalProductContextsForTenant(tenantId);
  if (!valid) {
    if (upper === 'ALL') return { title, url, product_context: 'ALL' };
    return { title, url, product_context: null };
  }
  if (!upper || upper === 'ALL' || valid.includes(upper)) {
    return { title, url, product_context: upper && valid.includes(upper) ? upper : 'ALL' };
  }
  return { title, url, product_context: 'ALL' };
}

export function serializeEducationalResourcesForApi(rows, tenantId) {
  const valid = getEducationalProductContextsForTenant(tenantId);
  const out = [];
  for (const row of rows || []) {
    const title = String(row.title || '').trim().slice(0, 200);
    const url = String(row.url || '').trim();
    if (!title && !url) continue;
    let product_context = row.product_context;
    if (product_context === '' || product_context == null) {
      product_context = valid ? 'ALL' : null;
    } else {
      const u = String(product_context).trim().toUpperCase();
      if (valid && (u === 'ALL' || valid.includes(u))) product_context = u;
      else product_context = valid ? 'ALL' : null;
    }
    out.push({ title, url, product_context });
  }
  return out;
}

/**
 * Allowed product_context values for educational resources on this account (Evasys SYS/EXAM, Learnwise SUPPORT/FEEDBACK/TUTOR).
 * Returns null when tenant does not use product-scoped educational resources (show all).
 */
export function resolveAllowedEducationalProductContexts(account, tenantId, persona) {
  const tenant = normalizeEducationalTenantId(
    tenantId || account?.tenant_id || persona?.tenant_id,
  );
  if (!tenantUsesEducationalProductContext(tenant)) return null;

  if (isSysExamProductContextTenant(tenant)) {
    const personPc = String(persona?.product_affinity ?? '').trim().toUpperCase();
    if (personPc === 'SYS' || personPc === 'EXAM') return [personPc];

    let list = getEvasysTargetProductListForContext(account?.target_products);
    if (list.length === 0 && account) {
      const hasEval = Boolean(account.evaluation_vendor);
      const hasAssess = Boolean(account.assessment_vendor);
      if (hasEval && !hasAssess) list = ['SYS'];
      else if (hasAssess && !hasEval) list = ['EXAM'];
      else if (hasEval) list = ['SYS'];
      else if (hasAssess) list = ['EXAM'];
    }
    return list.length > 0 ? list : null;
  }

  if (tenant === 'learnwise' || tenant === 'gravyty') {
    const personPc = String(persona?.product_affinity ?? '').trim().toUpperCase();
    if (['SUPPORT', 'FEEDBACK', 'TUTOR'].includes(personPc)) return [personPc];

    const derived = deriveLearnwiseTargetProductValue(account?.target_products);
    if (derived === 'ALL') return ['SUPPORT', 'FEEDBACK', 'TUTOR'];
    if (derived) return [derived];
    if (!account) return null;
    const inferred = [];
    if (account.tutor_vendor) inferred.push('TUTOR');
    if (account.feedback_vendor) inferred.push('FEEDBACK');
    if (account.support_vendor) inferred.push('SUPPORT');
    return inferred.length > 0 ? [...new Set(inferred)] : null;
  }

  if (tenant === 'cayuse') {
    const personPc = String(persona?.product_affinity ?? '').trim().toUpperCase();
    if (CAYUSE_PRODUCT_CONTEXTS.includes(personPc)) return [personPc];

    const list = getCayuseTargetProductListForContext(account?.target_products);
    return list.length > 0 ? list : null;
  }

  return null;
}

/**
 * Rows with product_context ALL or unset match any lane.
 * Tenant-scoped: drops other tenants' tags (e.g. SYS on learnwise) even when allowedContexts is null.
 */
export function filterEducationalResourcesByProductContext(rows, allowedContexts, tenantId) {
  if (!Array.isArray(rows)) return [];
  const tenantContexts = getEducationalProductContextsForTenant(tenantId);
  if (!tenantContexts) return rows;

  const allowed = new Set(
    (allowedContexts?.length
      ? allowedContexts
      : tenantContexts.filter((c) => c !== 'ALL')
    )
      .map((c) => String(c).trim().toUpperCase())
      .filter(Boolean),
  );

  return rows.filter((row) => {
    let pc = row?.product_context;
    if (pc == null || pc === '') return true;
    const u = String(pc).trim().toUpperCase();
    if (u === 'ALL') return true;
    if (!tenantContexts.includes(u)) return false;
    return allowed.has(u);
  });
}

function composerAssetMatchesProductContext(asset, allowedContexts, tenantId) {
  if (!isEducationalResourceAsset(asset)) return true;
  const tenantContexts = getEducationalProductContextsForTenant(tenantId);
  if (!tenantContexts) return true;

  let pc = asset?.product_context;
  if (pc == null || pc === '') return true;
  const u = String(pc).trim().toUpperCase();
  if (u === 'ALL') return true;
  if (!tenantContexts.includes(u)) return false;

  if (!allowedContexts?.length) return true;
  const allowed = new Set(allowedContexts.map((c) => String(c).trim().toUpperCase()));
  return allowed.has(u);
}

/** Map settings rows → available-assets shape for OutreachComposer. */
export function educationalResourceRowsToAssets(rows) {
  return (rows || [])
    .map((row) => {
      const title = String(row?.title ?? row?.name ?? '').trim();
      const url = resolveAssetUrl(row);
      if (!title || !url) return null;
      const pc = row?.product_context ?? null;
      return { type: 'educational_resource', name: title, url, product_context: pc };
    })
    .filter(Boolean);
}

function resolveAssetUrl(item) {
  return String(item?.url ?? item?.link ?? item?.href ?? '').trim();
}

/** Canonical type for composer collateral vs warm-up reading sections. */
export function canonicalComposerAssetType(rawType) {
  const lower = String(rawType ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  if (
    lower === 'educational_resource' ||
    lower === 'educational' ||
    lower === 'education_resource' ||
    lower === 'education' ||
    lower === 'reading' ||
    lower === 'warm_up_reading'
  ) {
    return 'educational_resource';
  }
  if (lower === 'case_study' || lower === 'case_study_link') return 'case_study';
  if (lower === 'client_reference') return 'client_reference';
  if (lower === 'product_link' || lower === 'product') return 'product_link';
  return lower || '';
}

/** Stable key for composer checkbox state (URL-based, or name-only client references). */
export function getComposerAssetSelectionKey(asset) {
  const url = resolveAssetUrl(asset);
  if (url) return url;
  const type = canonicalComposerAssetType(asset?.type);
  const name = String(asset?.name ?? '').trim();
  if (type === 'client_reference' && name) return `client_reference::${name}`;
  return '';
}

function assetDedupeKey(asset) {
  const url = resolveAssetUrl(asset);
  if (url) return url;
  return getComposerAssetSelectionKey(asset) || null;
}

export function isEducationalResourceAsset(asset) {
  return canonicalComposerAssetType(asset?.type) === 'educational_resource';
}

function normalizeAssetFromApi(item) {
  const type = canonicalComposerAssetType(
    item?.type ?? item?.asset_type ?? item?.assetType ?? item?.category
  );
  const name = String(item?.name ?? item?.title ?? '').trim();
  const url = resolveAssetUrl(item);

  if (type === 'client_reference') {
    if (!name) return null;
    return {
      ...item,
      type: 'client_reference',
      name,
      url: url || null,
      product_key: item?.product_key ?? item?.productKey ?? null,
    };
  }

  if (!url) return null;
  return { ...item, type, name: name || url, url };
}

function tagAssetsWithEducationalUrls(assets, educationalRows) {
  const eduByUrl = new Map();
  (educationalRows || []).forEach((row) => {
    const url = resolveAssetUrl(row);
    const title = String(row?.title ?? row?.name ?? '').trim();
    if (url) eduByUrl.set(url, row);
  });
  if (eduByUrl.size === 0) return assets;

  return (assets || []).map((asset) => {
    if (canonicalComposerAssetType(asset?.type) === 'client_reference') {
      return asset;
    }
    const url = resolveAssetUrl(asset);
    const row = eduByUrl.get(url);
    if (!row) return asset;
    const title = String(row?.title ?? row?.name ?? '').trim();
    return {
      ...asset,
      url,
      type: 'educational_resource',
      name: String(asset?.name ?? asset?.title ?? title).trim() || title,
      product_context: row?.product_context ?? asset?.product_context ?? null,
    };
  });
}

/** Merge API assets with educational rows; dedupe by URL (educational type wins on conflict). */
export function mergeAvailableAssetsWithEducational(apiAssets, educationalRows) {
  const byKey = new Map();
  const add = (raw) => {
    const asset = normalizeAssetFromApi(raw);
    if (!asset) return;
    const key = assetDedupeKey(asset);
    if (!key) return;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, asset);
      return;
    }
    if (
      isEducationalResourceAsset(asset) &&
      !isEducationalResourceAsset(prev)
    ) {
      byKey.set(key, { ...prev, type: 'educational_resource', name: asset.name || prev.name });
    }
  };

  (apiAssets || []).forEach(add);
  educationalResourceRowsToAssets(educationalRows).forEach(add);
  return [...byKey.values()];
}

function finalizeComposerAsset(a) {
  const type = canonicalComposerAssetType(a?.type);
  if (type === 'client_reference') {
    const name = String(a?.name ?? '').trim();
    if (!name) return null;
    const url = resolveAssetUrl(a);
    return {
      ...a,
      type: 'client_reference',
      name,
      url: url || null,
      product_key: a?.product_key ?? a?.productKey ?? null,
    };
  }
  const url = resolveAssetUrl(a);
  if (!url) return null;
  return { ...a, url, type };
}

export function parseAvailableAssetsResponse(data) {
  const apiAssets = Array.isArray(data?.assets) ? data.assets : [];
  const embeddedEdu = Array.isArray(data?.educational_resources) ? data.educational_resources : [];
  return mergeAvailableAssetsWithEducational(apiAssets, embeddedEdu);
}

/** Tenant educational_resources from admin_atoms via user settings API. */
export async function fetchEducationalResourcesForTenant(userId, tenantId) {
  if (!userId || !tenantId) {
    logOutreachAssets('settings/user skipped', { reason: 'missing userId or tenantId', userId, tenantId });
    return [];
  }
  try {
    const res = await fetch(`${API_BASE_URL}/api/settings/user`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': String(userId),
        'x-tenant-id': String(tenantId),
      },
    });
    if (!res.ok) {
      logOutreachAssets('settings/user failed', { status: res.status, tenantId, userId });
      return [];
    }
    const data = await res.json();
    const fromRoot = data.educational_resources;
    const fromSettings = data.settings?.educational_resources;
    const raw = fromRoot ?? fromSettings;
    logOutreachAssets('settings/user ok', {
      tenantId,
      userId,
      hasRootArray: Array.isArray(fromRoot),
      hasSettingsArray: Array.isArray(fromSettings),
      count: Array.isArray(raw) ? raw.length : 0,
      sample: Array.isArray(raw)
        ? raw.slice(0, 3).map((r) => ({
            title: r?.title ?? r?.name,
            product_context: r?.product_context,
            url: String(r?.url ?? '').slice(0, 64),
          }))
        : null,
    });
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => normalizeEducationalResourceRow(row, tenantId));
  } catch (e) {
    console.warn(OUTREACH_ASSETS_LOG, 'settings/user error', e);
    return [];
  }
}

export async function loadComposerAvailableAssets({
  personId,
  /** When set (e.g. from PersonProfile), try UUID/Mongo id then business `person_id` for `/api/personas/:id/available-assets`. */
  personIdentity,
  /** Linked account — used to filter tenant educational resources by SYS/EXAM (etc.). */
  account,
  /** Persona — `product_affinity` overrides account when set (Evasys SYS/EXAM). */
  persona,
  tenantId,
  userId,
}) {
  const allowedProductContexts = resolveAllowedEducationalProductContexts(account, tenantId, persona);
  const headers = {
    'x-tenant-id': String(tenantId),
    'x-user-id': String(userId),
  };

  const idCandidates = personIdentity
    ? await collectPersonPublicationPathIdCandidates(personIdentity, {})
    : [personId].map((x) => (x != null ? String(x).trim() : '')).filter(Boolean);

  logOutreachAssets('load start', {
    personId,
    idCandidates,
    tenantId,
    userId,
    allowedProductContexts,
  });

  if (!idCandidates.length) {
    logOutreachAssets('load skipped', { reason: 'no idCandidates', personId, tenantId, userId });
    const eduRows = filterEducationalResourcesByProductContext(
      await fetchEducationalResourcesForTenant(userId, tenantId),
      allowedProductContexts,
      tenantId,
    );
    let merged = [];
    if (eduRows.length > 0) {
      merged = mergeAvailableAssetsWithEducational([], eduRows);
      merged = tagAssetsWithEducationalUrls(merged, eduRows);
    }
    return merged
      .map(finalizeComposerAsset)
      .filter(Boolean)
      .filter((a) => composerAssetMatchesProductContext(a, allowedProductContexts, tenantId));
  }

  const fetchAssetsForCandidates = async () => {
    let lastRes = null;
    for (const pid of idCandidates) {
      const assetsUrl = `${API_BASE_URL}/api/personas/${encodeURIComponent(pid)}/available-assets?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
      lastRes = await fetch(assetsUrl, { credentials: 'include', headers });
      if (lastRes.ok) return { res: lastRes, resolvedPersonId: pid };
    }
    return { res: lastRes, resolvedPersonId: idCandidates[idCandidates.length - 1] ?? personId };
  };

  const [assetsBundle, eduRowsRaw] = await Promise.all([
    fetchAssetsForCandidates(),
    fetchEducationalResourcesForTenant(userId, tenantId),
  ]);
  const eduRows = filterEducationalResourcesByProductContext(
    eduRowsRaw,
    allowedProductContexts,
    tenantId,
  );
  const assetsRes = assetsBundle.res;

  let merged = [];
  let rawApiAssets = [];
  if (assetsRes.ok) {
    const data = await assetsRes.json();
    rawApiAssets = Array.isArray(data?.assets) ? data.assets : [];
    logOutreachAssets('available-assets ok', {
      personId,
      status: assetsRes.status,
      rawCount: rawApiAssets.length,
      rawTypes: rawApiAssets.map((a) => a?.type ?? a?.asset_type ?? '(missing)'),
      embeddedEduCount: Array.isArray(data?.educational_resources)
        ? data.educational_resources.length
        : 0,
      rawSample: rawApiAssets.slice(0, 5).map((a) => ({
        type: a?.type,
        asset_type: a?.asset_type,
        name: a?.name ?? a?.title,
        url: resolveAssetUrl(a)?.slice(0, 64),
      })),
    });
    merged = parseAvailableAssetsResponse(data);
    logOutreachAssets('after parseAvailableAssetsResponse', {
      count: merged.length,
      types: merged.map((a) => a.type),
      items: merged.map(summarizeAsset),
    });
  } else {
    logOutreachAssets('available-assets failed', {
      personId,
      triedIds: idCandidates,
      status: assetsRes?.status,
      statusText: assetsRes?.statusText,
    });
  }

  const mergedBeforeSettings = merged.length;
  if (eduRows.length > 0) {
    merged = mergeAvailableAssetsWithEducational(merged, eduRows);
    logOutreachAssets('after merge settings edu rows', {
      eduRowsCount: eduRows.length,
      countBefore: mergedBeforeSettings,
      countAfter: merged.length,
      types: merged.map((a) => a.type),
    });
    merged = tagAssetsWithEducationalUrls(merged, eduRows);
    logOutreachAssets('after tagAssetsWithEducationalUrls', {
      count: merged.length,
      types: merged.map((a) => a.type),
    });
  }

  const final = merged
    .map(finalizeComposerAsset)
    .filter(Boolean)
    .filter((a) => composerAssetMatchesProductContext(a, allowedProductContexts, tenantId));

  const reading = final.filter((a) => a.type === 'educational_resource');
  logOutreachAssets('load done', {
    personId,
    resolvedPersonId: assetsBundle.resolvedPersonId,
    tenantId,
    apiOk: assetsRes.ok,
    eduRowsRawCount: eduRowsRaw?.length ?? 0,
    eduRowsCount: eduRows.length,
    allowedProductContexts,
    finalCount: final.length,
    readingCount: reading.length,
    finalTypes: final.map((a) => a.type),
    readingItems: reading.map(summarizeAsset),
    collateralCount: final.length - reading.length,
    finalItems: final.map(summarizeAsset),
  });

  return final;
}
